import * as React from 'react'

import { Box, Button, Table, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import UserAvatar from 'modules/core/components/UserAvatar'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import goalsConfig from 'modules/goals/store/collections/goalsconfig'
import type Goal from 'modules/goals/store/models/Goal'
import { PROGRESS_STATUS } from 'modules/goals/store/models/Goal'
import type GoalConfig from 'modules/goals/store/models/GoalConfig'

import Empty from './_Empty'
import Numeric from './_Numeric'

interface ActionsProps {
  goal: Goal
  goalConfig: GoalConfig
  showActions?: boolean
}

const Actions = observer(({ goal, goalConfig, showActions }: ActionsProps) => {
  const [open, toggle] = useToggle(false)

  const { performAction: unAssignEmployee } = useAsyncAction(async () => {
    await goal.destroy()

    return goalsConfig.fetch()
  })

  const options = [
    {
      onClick: () => unAssignEmployee(),
      title: i18n.t('surveys.goals.show.table.unassign'),
    },
  ]

  if (goalConfig.isFinished || !showActions || !goal.policy.can('deleteGoal')) {
    return (
      <Table.Cell colSpan={goalConfig.isNumeric ? 1 : 5}>
        <Box />
      </Table.Cell>
    )
  }

  return (
    <Table.Cell colSpan={goalConfig.isNumeric ? 1 : 5}>
      <Box flexDirection="row" justifyContent="flexEnd">
        <Popover attachment="top center" open={open} toggle={toggle}>
          <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
          <PopoverContent onClose={toggle(false)} options={options} />
        </Popover>
      </Box>
    </Table.Cell>
  )
})
interface GoalOwnerProps {
  goal: Goal
}

const GoalOwner = ({ goal }: GoalOwnerProps) => {
  const [open, toggle] = useToggle(false)
  if (!goal.user) return null

  return (
    <Table.Cell colSpan={3}>
      <Box flexDirection="row" gap="s16" alignItems="center">
        <UserAvatar size="s40" user={goal.user} />
        <Box overflowX="hidden">
          <Tooltip.Alt
            attachment="bottom center"
            open={open}
            toggle={toggle}
            label={goal.user.fullName()}
          >
            <Text.Normal weight="medium" ellipsis>
              {goal.user.fullName()}
            </Text.Normal>
          </Tooltip.Alt>
        </Box>
      </Box>
    </Table.Cell>
  )
}

interface Props {
  goal: Goal
  goalConfig: GoalConfig
  showActions?: boolean
}

const GoalRow = ({ goal, goalConfig, showActions }: Props) => {
  const options = [
    {
      value: PROGRESS_STATUS.overAchieved,
      label: i18n.t('surveys.goals.show.table.over_achieved'),
    },
    {
      value: PROGRESS_STATUS.achieved,
      label: i18n.t('surveys.goals.show.table.achieved'),
    },
    {
      value: PROGRESS_STATUS.onTrack,
      label: i18n.t('surveys.goals.show.table.on_track'),
    },
    {
      value: PROGRESS_STATUS.progressing,
      label: i18n.t('surveys.goals.show.table.progressing'),
    },
    {
      value: PROGRESS_STATUS.atRisk,
      label: i18n.t('surveys.goals.show.table.at_risk'),
    },
    {
      value: PROGRESS_STATUS.notAchieved,
      label: i18n.t('surveys.goals.show.table.not_achieved'),
    },
  ]

  const GoalTypeRows = () => {
    return goalConfig.isNumeric ? (
      <Numeric goal={goal} goalConfig={goalConfig} options={options} />
    ) : (
      <Empty goal={goal} goalConfig={goalConfig} options={options} />
    )
  }

  return (
    <Table.Row>
      <GoalOwner goal={goal} />
      <GoalTypeRows />
      <Actions showActions={showActions} goal={goal} goalConfig={goalConfig} />
    </Table.Row>
  )
}

export default observer(GoalRow)
